import PropTypes from 'prop-types';
import React, { useRef, useEffect } from 'react';
import getWidth from 'dom-helpers/width';
import { formatDate } from '../utils/dateutils';

import { navigate } from 'react-big-calendar/lib/utils/constants';
import { inRange } from 'react-big-calendar/lib/utils/eventLevels';
import { isSelected } from 'react-big-calendar/lib/utils/selection';

// Helper function to get the first day of the month
// date: The date object from which the first day of the month is derived
const getFirstDayOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

// Helper function to get the last day of the month
// date: The date object from which the last day of the month is derived
const getLastDayOfMonth = (date) => {
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return lastDate;
}

function Agenda({
  accessors,
  components,
  date,
  events,
  getters,
  localizer,
  onDoubleClickEvent,
  onSelectEvent,
  selected,
}) {
  const headerRef = useRef(null);
  const dateColRef = useRef(null);
  const timeColRef = useRef(null);
  const eventColRef = useRef(null);
  const descriptionColRef = useRef(null);
  const venueColRef = useRef(null);
  const contentRef = useRef(null);
  const tbodyRef = useRef(null);
  const rowRefs = useRef([]);

  const columnOrder = Agenda.columnOrder || ['date', 'time', 'title', 'description', 'venue', 'link'];

  // useEffect to adjust header on mount and update
  useEffect(() => {
    _adjustHeader()
  })

  // useEffect to scroll to the specific event row when the date changes
  useEffect(() => {
    const formattedDate = formatDate(date);
    const tableContainer = document.querySelector('.rbc-agenda-view');
    const rows = document.querySelectorAll('.rbc-agenda-view tbody tr');
    const tableHeader = document.querySelector('.rbc-agenda-table thead');
    const rowIndex = Array.from(rows).findIndex(event => (event.dataset.eventStart) === formattedDate);

    if (rows.length > 0 && rows[rowIndex]) {
      let rowPosition = rows[rowIndex].offsetTop;
      if (tableHeader && tableHeader.offsetHeight) {
        rowPosition -= tableHeader.offsetHeight;
      }
      tableContainer.scroll({
        top: rowPosition ? rowPosition : 0, // Scroll to the row's position
        behavior: 'smooth', // Smooth scrolling
      });
    }

  }, [date, events]);

  // Renders the events for each day in the agenda view
  // day: The current day in the range being rendered
  // events: Array of events for the given day
  // dayKey: Unique key for each day in the range
  const renderDay = (day, events, dayKey) => {
    const { event: Event, date: AgendaDate } = components;

    events = events.filter((e) =>
      inRange(
        e,
        localizer.startOf(day, 'day'),
        localizer.endOf(day, 'day'),
        accessors,
        localizer
      )
    )

    return events.map((event, idx) => {
      let title = accessors.title(event)
      let description = accessors.description ? accessors.description(event) : event.description;
      let venue = accessors.venue ? accessors.venue(event) : event.venue;
      let details_url = accessors.details_url ? accessors.details_url(event) : event.details_url;
      let link = accessors.link ? accessors.link(event) : event.link;
      let end = accessors.end(event)
      let start = accessors.start(event)

      const userProps = getters.eventProp(
        event,
        start,
        end,
        isSelected(event, selected)
      )

      let dateLabel = localizer.format(day, 'agendaDateFormat')

      return (
        <tr
          key={dayKey + '_' + idx}
          ref={el => rowRefs.current[idx] = el}
          className={userProps.className}
          data-event-start={formatDate(event.start)}
          style={{ ...userProps.style }}
        >
          {columnOrder.map((column) => {
            if (column === 'date') {
              return (
                <td key={column} className="rbc-agenda-date-cell">
                  {AgendaDate ? (
                    <AgendaDate day={day} label={dateLabel} />
                  ) : (
                    dateLabel
                  )}
                </td>
              );
            }
            if (column === 'time') {
              return (
                <td key={column} className="rbc-agenda-time-cell">
                  {timeRangeLabel(day, event)}
                </td>
              );
            }
            if (column === 'title') {
              return (
                <td
                  key={column}
                  className="rbc-agenda-event-cell"
                  style={{ color: event.text_color }}
                  onClick={(e) => onSelectEvent && onSelectEvent(event, e)}
                  onDoubleClick={(e) =>
                    onDoubleClickEvent && onDoubleClickEvent(event, e)
                  }
                >
                  <strong style={{backgroundColor: event.legend_color}}>{Event ? <Event event={event} title={title} /> : title}</strong>
                </td>
              );
            }
            if (column === 'description') {
              return <td key={column} className="rbc-agenda-description-cell">{description}</td>;
            }
            if (column === 'venue') {
              return (
                <td key={column} className="rbc-agenda-venue-cell">
                  {link ? <a href={link} target="_blank" rel="noopener noreferrer">{venue || link}</a> : null}
                </td>
              );
            }
            if (column === 'link') {
              return (
                <td key={column} className="rbc-agenda-link-cell">
                  {details_url ? <a href={details_url} target="_blank" rel="noopener noreferrer">{messages.eventLinkText || details_url}</a> : null}
                </td>
              );
            }
            return null;
          })}
        </tr>
      )
    }, [])
  }

  // Generates the time range label for each event
  // day: The current day
  // event: The event object
  const timeRangeLabel = (day, event) => {
    let labelClass = '',
      TimeComponent = components.time,
      label = localizer.messages.allDay

    let end = accessors.end(event)
    let start = accessors.start(event)

    if (!accessors.allDay(event)) {
      if (localizer.eq(start, end)) {
        label = localizer.format(start, 'agendaTimeFormat')
      } else if (localizer.isSameDate(start, end)) {
        label = localizer.format({ start, end }, 'agendaTimeRangeFormat')
      } else if (localizer.isSameDate(day, start)) {
        label = localizer.format(start, 'agendaTimeFormat')
      } else if (localizer.isSameDate(day, end)) {
        label = localizer.format(end, 'agendaTimeFormat')
      }
    }

    if (localizer.gt(day, start, 'day')) labelClass = 'rbc-continues-prior'
    if (localizer.lt(day, end, 'day')) labelClass += ' rbc-continues-after'

    return (
      <span className={labelClass.trim()}>
        {TimeComponent ? (
          <TimeComponent event={event} day={day} label={label} />
        ) : (
          label
        )}
      </span>
    )
  }

  // Adjusts the header column widths based on the table rows
  const _adjustHeader = () => {
    if (!tbodyRef.current) return;

    let header = headerRef.current;
    let firstRow = tbodyRef.current.firstChild;

    if (!firstRow) return;

    let columnWidths = [];

    // Check widths for every row, not just the first one
    Array.from(tbodyRef.current.children).forEach(row => {
      Array.from(row.children).forEach((cell, idx) => {
        columnWidths[idx] = Math.max(columnWidths[idx] || 0, getWidth(cell)); // Get the max width of each column
      });
    });

    columnWidths.forEach((width, idx) => {
      const columns = [
        dateColRef,
        timeColRef,
        eventColRef, // Event cell width
        descriptionColRef, // Description cell width
        venueColRef, // Venue cell width
      ];
      if (columns[idx] && columns[idx].current) {
        columns[idx].current.style.width = `${width}px`;
      }
    });
  };

  let { messages } = localizer
  
  // Get the first and last day of the month for the given date
  const startOfMonth = getFirstDayOfMonth(date);
  const endOfMonth = getLastDayOfMonth(date);

  let range = localizer.range(startOfMonth, endOfMonth, 'day');

  events = events.filter((event) =>
    inRange(
      event,
      localizer.startOf(startOfMonth, 'day'),
      localizer.endOf(endOfMonth, 'day'),
      accessors,
      localizer
    )
  )

  events.sort((a, b) => +accessors.start(a) - +accessors.start(b))

  return (
    <div className="rbc-agenda-view">
      {events.length !== 0 ? (
        <React.Fragment>
          <table ref={headerRef} className="rbc-agenda-table">
            <thead>
              <tr>
                {columnOrder.map((column) => {
                  if (column === 'date') return <th key={column} className="rbc-header" ref={dateColRef}>{messages.date}</th>;
                  if (column === 'time') return <th key={column} className="rbc-header" ref={timeColRef}>{messages.time}</th>;
                  if (column === 'title') return <th key={column} className="rbc-header" ref={eventColRef}>{messages.event}</th>;
                  if (column === 'description') return <th key={column} className="rbc-header" ref={descriptionColRef}>{messages.eventDescription}</th>;
                  if (column === 'venue') return <th key={column} className="rbc-header" ref={venueColRef}>{messages.eventVenue}</th>;
                  if (column === 'link') return <th key={column} className="rbc-header">{messages.eventLink}</th>;
                  return null;
                })}
              </tr>
            </thead>
            <tbody ref={tbodyRef}>
                {range.map((day, idx) => renderDay(day, events, idx))}
              </tbody>
          </table>
        </React.Fragment>
      ) : (
        <span className="rbc-agenda-empty">{messages.noEventsInRange}</span>
      )}
    </div>
  )
}

Agenda.propTypes = {
  accessors: PropTypes.object.isRequired,
  components: PropTypes.object.isRequired,
  date: PropTypes.instanceOf(Date),
  events: PropTypes.array,
  getters: PropTypes.object.isRequired,
  localizer: PropTypes.object.isRequired,
  onSelectEvent: PropTypes.func,
  onDoubleClickEvent: PropTypes.func,
  selected: PropTypes.object,
}

Agenda.range = (start, { localizer }) => {
  const startOfMonth = getFirstDayOfMonth(start);
  const endOfMonth = getLastDayOfMonth(start);
  return { start: startOfMonth, end: endOfMonth };
}

Agenda.navigate = (
  date,
  action,
  { localizer }
) => {
  switch (action) {
    case navigate.PREVIOUS:
      // Navigate to the previous month
      const prevMonth = new Date(date);
      prevMonth.setMonth(date.getMonth() - 1);
      return getFirstDayOfMonth(prevMonth);

    case navigate.NEXT:
      // Navigate to the next month
      const nextMonth = new Date(date);
      nextMonth.setMonth(date.getMonth() + 1);
      return getFirstDayOfMonth(nextMonth);

    default:
      return date;
  }
}

Agenda.title = (start, { localizer }) => {
  const startOfMonth = getFirstDayOfMonth(start);
  const endOfMonth = getLastDayOfMonth(start);
  return localizer.format({ start: startOfMonth, end: endOfMonth }, 'agendaHeaderFormat');
}

export default Agenda;
